/**
 * Check-In Status Badge
 *
 * Displays the current check-in/out status of a unit
 */

import { LogIn, LogOut, Clock } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { formatDistanceToNow } from 'date-fns';
import type { CheckinCurrentStatus } from '@/types/checkin';
import { checkinSignerName } from '@/lib/checkinSigner';

interface CheckinStatusBadgeProps {
  status: CheckinCurrentStatus | null;
  isLoading?: boolean;
  onClick?: () => void;
  variant?: 'default' | 'compact';
}

export function CheckinStatusBadge({
  status,
  isLoading,
  onClick,
  variant = 'default',
}: CheckinStatusBadgeProps) {
  if (isLoading) {
    return (
      <Badge variant="outline" className="animate-pulse">
        <Clock className="w-3 h-3 mr-1" />
        Loading...
      </Badge>
    );
  }

  if (!status) {
    return (
      <Badge
        variant="outline"
        className="text-zinc-500 border-zinc-600 cursor-default"
      >
        <LogIn className="w-3 h-3 mr-1" />
        No check-ins
      </Badge>
    );
  }

  const isCheckedIn = status.event_type === 'check_in';
  const driverName = checkinSignerName(status) || 'Unknown';
  const timeAgo = formatDistanceToNow(new Date(status.event_timestamp), { addSuffix: true });

  const badgeContent = variant === 'compact' ? (
    <>
      {isCheckedIn ? (
        <LogOut className="w-3 h-3 mr-1" />
      ) : (
        <LogIn className="w-3 h-3 mr-1" />
      )}
      {isCheckedIn ? 'Checked In' : 'Checked Out'}
    </>
  ) : (
    <>
      {isCheckedIn ? (
        <LogOut className="w-3 h-3 mr-1" />
      ) : (
        <LogIn className="w-3 h-3 mr-1" />
      )}
      {isCheckedIn ? 'In' : 'Out'}: {driverName}
    </>
  );

  const badge = (
    <Badge
      variant="outline"
      className={`
        ${onClick ? 'cursor-pointer hover:bg-accent' : 'cursor-default'}
        ${isCheckedIn
          ? 'border-green-500/50 text-green-400 bg-green-500/10'
          : 'border-amber-500/50 text-amber-400 bg-amber-500/10'
        }
      `}
      onClick={onClick}
    >
      {badgeContent}
    </Badge>
  );

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          {badge}
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-xs">
          <div className="text-sm">
            <p className="font-medium">
              {isCheckedIn ? 'Checked in by' : 'Checked out by'} {driverName}
            </p>
            <p className="text-zinc-400 text-xs mt-1">
              {timeAgo}
            </p>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
